import React from 'react';

/**
 * A small red count badge for a sidebar nav item.
 *
 * This is the FIRST badge in CollapsibleDashboard's nav, so it sets the
 * precedent: it is deliberately a dumb presentational component with no data
 * fetching, no permission logic and no knowledge of what it is counting — the
 * caller owns all of that. A second badge should reuse this rather than grow
 * it into a framework.
 *
 * Contract:
 *  - Nothing renders at zero (or a negative / non-finite count). An empty
 *    circle or a "0" is noise that trains people to ignore the real thing.
 *  - The DISPLAY caps at "99+", but the accessible name and the tooltip keep
 *    the true number, so nobody has to guess whether 99+ means 100 or 4,000.
 *  - `unitLabel` is composed WITH the count here rather than passed in
 *    pre-rendered: that is what guarantees the title cannot drift from the
 *    number when the display is capped.
 */
export interface NavCountBadgeProps {
  count: number;
  /** Plural noun phrase, e.g. "open maintenance requests". */
  unitLabel: string;
  className?: string;
}

/** Above this the badge would outgrow the nav row. */
const DISPLAY_CAP = 99;

export const NavCountBadge: React.FC<NavCountBadgeProps> = ({ count, unitLabel, className }) => {
  if (!Number.isFinite(count) || count <= 0) return null;

  const label = `${count} ${unitLabel}`;

  return (
    <span
      data-testid="nav-count-badge"
      role="status"
      aria-label={label}
      title={label}
      className={
        'ms-auto inline-flex h-5 min-w-[1.25rem] flex-shrink-0 items-center justify-center ' +
        'rounded-full px-1.5 text-[0.6875rem] font-semibold leading-none ' +
        'bg-red-600 text-white ring-1 ring-red-700/40 ' +
        'dark:bg-red-500 dark:text-white dark:ring-red-300/40' +
        (className ? ` ${className}` : '')
      }
    >
      {count > DISPLAY_CAP ? `${DISPLAY_CAP}+` : String(count)}
    </span>
  );
};

export default NavCountBadge;
